import type { HttpQueryParam, IHttpClient } from "@/core/interfaces";
import { Registry } from "@/shared/infra/dependency-injection/registry";

import {
  cabecalhoIdempotencia,
  readEnvelopeData,
} from "../financeiro-obras/financeiro-obras.gateway";
import type {
  LookupRequestOptions,
  OpcoesMutacao,
} from "../financeiro-obras/financeiro-obras.gateway";
import type {
  GrupoBaseCriado,
  LinhaEstrutura,
  ListarEstruturaBaseParams,
  NovoGrupoBaseBody,
  SituacaoBase,
  SituacaoBaseAlterada,
  TipoObraBase,
} from "./financeiro-configuracoes.base-dados.types";

export const BASE_DADOS_PATHS = {
  TIPOS_OBRA: "/api/financeiro/configuracoes/bases-referencia/tipos-obra",
  SITUACAO_TIPO_OBRA: (tipoObraId: string) =>
    `/api/financeiro/configuracoes/bases-referencia/tipos-obra/${encodeURIComponent(tipoObraId)}/situacao`,
  GRUPOS: "/api/financeiro/configuracoes/bases-referencia/grupos",
  SITUACAO_GRUPO: (grupoId: string) =>
    `/api/financeiro/configuracoes/bases-referencia/grupos/${encodeURIComponent(grupoId)}/situacao`,
  SITUACAO_SUBGRUPO: (grupoId: string, subgrupoId: string) =>
    `/api/financeiro/configuracoes/bases-referencia/grupos/${encodeURIComponent(grupoId)}/subgrupos/${encodeURIComponent(subgrupoId)}/situacao`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

export async function getTiposObraBase({ signal }: LookupRequestOptions = {}): Promise<
  TipoObraBase[]
> {
  const response = await getHttpClient().get<unknown>(BASE_DADOS_PATHS.TIPOS_OBRA, { signal });
  const tipos = readEnvelopeData<TipoObraBase[]>(response.data);
  if (!Array.isArray(tipos)) throw new Error("Resposta inválida do bff.");
  return tipos;
}

export async function getEstruturaBase(
  { incluirInativos }: ListarEstruturaBaseParams = {},
  { signal }: LookupRequestOptions = {},
): Promise<LinhaEstrutura[]> {
  const params: Record<string, HttpQueryParam> = {};
  if (incluirInativos !== undefined) params.incluirInativos = incluirInativos;

  const response = await getHttpClient().get<unknown>(BASE_DADOS_PATHS.GRUPOS, { params, signal });
  const linhas = readEnvelopeData<LinhaEstrutura[]>(response.data);
  if (!Array.isArray(linhas)) throw new Error("Resposta inválida do bff.");
  return linhas;
}

export async function criarGrupoBase(
  body: NovoGrupoBaseBody,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<GrupoBaseCriado> {
  const response = await getHttpClient().post<unknown>(BASE_DADOS_PATHS.GRUPOS, body, {
    headers: cabecalhoIdempotencia(idempotencyKey),
  });
  return readEnvelopeData<GrupoBaseCriado>(response.data);
}

export async function alterarSituacaoTipoObra(
  tipoObraId: string,
  situacao: SituacaoBase,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<SituacaoBaseAlterada> {
  const response = await getHttpClient().patch<unknown>(
    BASE_DADOS_PATHS.SITUACAO_TIPO_OBRA(tipoObraId),
    { situacao },
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<SituacaoBaseAlterada>(response.data);
}

export async function alterarSituacaoGrupo(
  grupoId: string,
  situacao: SituacaoBase,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<SituacaoBaseAlterada> {
  const response = await getHttpClient().patch<unknown>(
    BASE_DADOS_PATHS.SITUACAO_GRUPO(grupoId),
    { situacao },
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<SituacaoBaseAlterada>(response.data);
}

export async function alterarSituacaoSubgrupo(
  grupoId: string,
  subgrupoId: string,
  situacao: SituacaoBase,
  { idempotencyKey }: OpcoesMutacao = {},
): Promise<SituacaoBaseAlterada> {
  const response = await getHttpClient().patch<unknown>(
    BASE_DADOS_PATHS.SITUACAO_SUBGRUPO(grupoId, subgrupoId),
    { situacao },
    { headers: cabecalhoIdempotencia(idempotencyKey) },
  );
  return readEnvelopeData<SituacaoBaseAlterada>(response.data);
}
